import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowUp, ArrowDown, Trash2, Plus, Save, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { NotesEditor, MediaEditor } from './AdminGuideExtras';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AREAS = [['all', 'Tutti i clienti'], ['financial', 'Financial Risk'], ['compliance', 'HR & Compliance'], ['security', 'Sicurezza'], ['cyber', 'Cyber Security'], ['aml_api', 'API AML']];
const box = 'w-full bg-[#050505] border border-[#1F242C] text-white text-sm p-2 rounded-sm focus:outline-none focus:border-[#D4AF37]';

function SectionEditor({ s, i, n, set, move, remove, headers }) {
  return (
    <div className="border border-[#1F242C] p-4 space-y-3" data-testid={`admin-guide-section-${i}`}>
      <div className="flex flex-wrap gap-2">
        <Input value={s.title} onChange={(e) => set({ title: e.target.value })} placeholder="Titolo della sezione" className="h-9 flex-1 min-w-[200px] bg-black border-[#1F242C] text-white" data-testid={`admin-guide-title-${i}`} />
        <select value={s.area} onChange={(e) => set({ area: e.target.value })} className="h-9 bg-black border border-[#1F242C] text-white text-sm px-2 rounded-sm" data-testid={`admin-guide-area-${i}`}>
          {AREAS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button onClick={() => move(-1)} disabled={i === 0} className="h-9 w-9 flex items-center justify-center border border-[#1F242C] text-[#8A8F97] disabled:opacity-30" data-testid={`admin-guide-up-${i}`}><ArrowUp size={14} /></button>
        <button onClick={() => move(1)} disabled={i === n - 1} className="h-9 w-9 flex items-center justify-center border border-[#1F242C] text-[#8A8F97] disabled:opacity-30" data-testid={`admin-guide-down-${i}`}><ArrowDown size={14} /></button>
        <button onClick={remove} className="h-9 w-9 flex items-center justify-center border border-[#1F242C] text-[#8A8F97] hover:text-[#E37A72]" data-testid={`admin-guide-delete-${i}`}><Trash2 size={14} /></button>
      </div>
      <label className="block text-[11px] uppercase tracking-wider text-[#7A7F87]">Testo libero (facoltativo)
        <textarea rows={3} value={s.text} onChange={(e) => set({ text: e.target.value })} className={`${box} mt-1 normal-case tracking-normal`} data-testid={`admin-guide-text-${i}`} />
      </label>
      <label className="block text-[11px] uppercase tracking-wider text-[#7A7F87]">Punti elenco (uno per riga)
        <textarea rows={5} value={s.itemsText} onChange={(e) => set({ itemsText: e.target.value })} className={`${box} mt-1 normal-case tracking-normal`} data-testid={`admin-guide-items-${i}`} />
      </label>
      <NotesEditor notes={s.notes || []} onChange={(notes) => set({ notes })} idx={i} />
      <MediaEditor media={s.media || []} onChange={(media) => set({ media })} idx={i} headers={headers} />
    </div>
  );
}

export default function AdminGuidePanel({ headers }) {
  const [sections, setSections] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = (data) => setSections(data.map((s) => ({ ...s, itemsText: (s.items || []).join('\n') })));
  useEffect(() => { axios.get(`${API}/admin/portal/guide`, { headers }).then((r) => load(r.data)).catch(() => toast.error('Guida non caricata')); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (i, patch) => setSections(sections.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const move = (i, d) => { const next = [...sections]; [next[i], next[i + d]] = [next[i + d], next[i]]; setSections(next); };
  const add = () => setSections([...sections, { title: '', area: 'all', text: '', itemsText: '', notes: [], media: [] }]);
  const save = async () => {
    if (sections.some((s) => !s.title.trim())) { toast.error('Ogni sezione deve avere un titolo'); return; }
    setBusy(true);
    try {
      const body = { sections: sections.map(({ itemsText, ...s }) => ({ ...s, items: itemsText.split('\n'), notes: (s.notes || []).filter((x) => x.text.trim()), media: (s.media || []).filter((x) => x.url.trim()) })) };
      load((await axios.put(`${API}/admin/portal/guide`, body, { headers })).data);
      toast.success('Guida salvata');
    } catch (e) { toast.error(e?.response?.data?.detail?.toString() || 'Salvataggio non riuscito'); } finally { setBusy(false); }
  };

  if (!sections) return <Loader2 className="animate-spin text-[#D4AF37]" />;
  return (
    <div className="bg-[#0B0D10] border border-[#1F242C] p-6 space-y-4" data-testid="admin-guide-panel">
      <div className="flex flex-wrap justify-between items-center gap-2">
        <h3 className="font-heading text-sm uppercase tracking-widest text-[#BFBFBF]">Guida e istruzioni del portale</h3>
        <div className="flex gap-2">
          <Button onClick={add} variant="outline" className="h-9 border-[#1F242C] bg-transparent text-white rounded-sm" data-testid="admin-guide-add"><Plus size={14} className="mr-2" />Sezione</Button>
          <Button onClick={save} disabled={busy} className="h-9 bg-[#D4AF37] text-black rounded-sm" data-testid="admin-guide-save">{busy ? <Loader2 size={14} className="animate-spin" /> : <><Save size={14} className="mr-2" />Salva</>}</Button>
        </div>
      </div>
      <p className="text-xs text-[#5A5F67]">Ogni sezione compare solo ai clienti che hanno l'area scelta abilitata ("Tutti i clienti" la mostra a tutti).</p>
      {sections.map((s, i) => (
        <SectionEditor key={s.id || `new-${i}`} s={s} i={i} n={sections.length} set={(p) => update(i, p)} move={(d) => move(i, d)} remove={() => setSections(sections.filter((_, j) => j !== i))} headers={headers} />
      ))}
    </div>
  );
}
